import { useState } from "react";
import { Check, CaretUpDown, MagnifyingGlass } from "@phosphor-icons/react";
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

/**
 * Searchable single-select combobox.
 * Drop-in replacement for the native <select> we use across creation dialogs &
 * list filters where the option list (clients, invoices, etc.) is too long for
 * a plain dropdown.
 *
 * Props:
 *   options:     [{ value, label, hint? }]  // hint = optional secondary text (e.g. GSTIN)
 *   value:       current selected value (string)
 *   onChange:    (value) => void
 *   placeholder: shown when no value chosen (default: "Select…")
 *   searchPlaceholder: text in the search input (default: "Type to search…")
 *   emptyText:   shown when no matches (default: "No matches.")
 *   testId:      base data-testid; trigger = `${testId}-trigger`, item = `${testId}-item-{value}`
 *   disabled:    bool
 *   required:    bool — visual marker only; native form validation still uses the underlying value
 */
export const SearchableSelect = ({
    options = [],
    value,
    onChange,
    placeholder = "Select…",
    searchPlaceholder = "Type to search…",
    emptyText = "No matches.",
    testId,
    disabled = false,
    className,
}) => {
    const [open, setOpen] = useState(false);
    const selected = options.find((o) => o.value === value);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    data-testid={testId ? `${testId}-trigger` : undefined}
                    className={cn(
                        "w-full justify-between font-normal h-10 px-3 text-sm border-slate-200 bg-white hover:bg-white",
                        !selected && "text-slate-500",
                        className
                    )}
                >
                    <span className="truncate">{selected ? selected.label : placeholder}</span>
                    <CaretUpDown size={14} className="ml-2 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="p-0 w-[--radix-popover-trigger-width]"
                align="start"
                data-testid={testId ? `${testId}-popover` : undefined}
            >
                <Command>
                    <CommandInput
                        placeholder={searchPlaceholder}
                        data-testid={testId ? `${testId}-search` : undefined}
                    />
                    <CommandList>
                        <CommandEmpty className="py-6 text-center text-sm text-slate-500">
                            <MagnifyingGlass size={20} className="mx-auto mb-2 text-slate-300" />
                            {emptyText}
                        </CommandEmpty>
                        <CommandGroup>
                            {options.map((opt) => (
                                <CommandItem
                                    key={opt.value}
                                    value={`${opt.label} ${opt.hint || ""} ${opt.value}`}
                                    onSelect={() => {
                                        onChange(opt.value);
                                        setOpen(false);
                                    }}
                                    data-testid={testId ? `${testId}-item-${opt.value}` : undefined}
                                    className="cursor-pointer"
                                >
                                    <Check
                                        size={14}
                                        className={cn(
                                            "mr-2",
                                            value === opt.value ? "opacity-100" : "opacity-0"
                                        )}
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-sm">{opt.label}</div>
                                        {opt.hint && (
                                            <div className="truncate text-[11px] text-slate-500 mt-0.5">
                                                {opt.hint}
                                            </div>
                                        )}
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export default SearchableSelect;
